import { rgba } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { theme, ifProp, withProp } from 'styled-tools';

import Box from '@ui/box/Box.tsx';
import Text from '@ui/text/Text.tsx';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import SearchResultsIndicator from './SearchResultsIndicator.tsx';

const LevelRowContainer = styled(Box)`
  border-bottom: ${withProp(theme('colors.primaryText'), primaryText => `1px solid ${rgba(primaryText, 0.1)}`)};

  &:focus-visible {
    outline-offset: -3px;
  }
`;

const LevelRowWrapper = styled(Box)`
  cursor: pointer;
  padding-block: 6px 5px;
  padding-inline: 16px 0;

  &:hover {
    background-color: ${withProp(theme('colors.primary'), primary => rgba(primary, 0.05))};
  }

  ${ifProp(
    '$isSelected',
    css`
      background-color: ${withProp(theme('colors.primary'), primary => rgba(primary, 0.05))};
      &::before {
        content: '';
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        height: 100%;
        width: 3px;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        background-color: ${theme('colors.primary')};
      }
    `,
    '',
  )}
`;

const LevelRowText = styled(Text)`
  word-break: break-word;
`;

const LevelRowContent = styled(Box)`
  padding-inline-end: 6px;
`;

const StyledSearchResultsIndicator = styled(SearchResultsIndicator)`
  margin-inline-end: 6px;
`;

// treeTabIndex / treeOnKeyDown / isTreeFocused are injected for ARIA tree roving tabindex.
const LevelRow = ({ ref, ...props }) => {
  const colors = useCSSColors();
  const {
    id,
    name,
    details,
    resultsNumber,
    selectedLevelId,
    onLevelClick,
    buildingName,
    treeTabIndex,
    treeOnKeyDown,
    isTreeFocused,
    T,
  } = props;

  const createAriaLabel = resultsNumber => {
    return resultsNumber
      ? `${buildingName} ${name} ${details}, ${T('mapLevelSelectorOnline:_resultsNumber_ search result within this level', { count: resultsNumber, resultsNumber })}`
      : `${buildingName} ${name} ${details}`; // If there are no results just the name
  };

  return (
    <LevelRowContainer
      data-testid="LevelRow"
      ref={ref}
      bg={colors.background}
      role="treeitem"
      aria-label={createAriaLabel(resultsNumber)} // If there are no results just the time
      onClick={() => onLevelClick(id)}
      aria-selected={selectedLevelId && selectedLevelId === id}
      tabIndex={typeof treeTabIndex === 'number' ? treeTabIndex : 0}
      onKeyDown={treeOnKeyDown || (e => (e.key === 'Enter' || e.key === ' ' ? onLevelClick(id) : null))}
      data-tree-focused={isTreeFocused ? 'true' : undefined}
    >
      <LevelRowWrapper
        display="flex"
        position="relative"
        alignItems="center"
        justifyContent="space-between"
        $isSelected={selectedLevelId && selectedLevelId === id}
      >
        <LevelRowContent display="flex" flexDirection="column" alignItems="flex-start">
          <LevelRowText
            fontSize="md"
            fontWeight={500}
            lineHeight="18px"
            color={colors.primaryText}
            limitLinesOfTextTo={1}
            aria-hidden="true"
          >
            {name}
          </LevelRowText>
          <LevelRowText fontSize="sm" color={colors.secondaryText} limitLinesOfTextTo={1} aria-hidden="true">
            {details}
          </LevelRowText>
        </LevelRowContent>

        {resultsNumber && <StyledSearchResultsIndicator resultsNumber={resultsNumber} />}
      </LevelRowWrapper>
    </LevelRowContainer>
  );
};

LevelRow.displayName = 'Level Row';

export default LevelRow;
